import React, { useState, useEffect } from 'react'
import { Form, Input, Button, Typography, Alert, Card, Space, Row, Col, Modal, message } from 'antd'
import { TruckOutlined, FileTextOutlined, BankOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import '../styles/global-input-fixes.css'
import './LoginRomaneio.css'
import { sanitizeEmpresa, sanitizePlaca, sanitizeRomaneio } from '../utils/securityUtils'

const { Title, Text } = Typography

const LoginRomaneio = ({ onSuccess, onCancel, autoNavigate = true }) => {
  const [form] = Form.useForm()
  const { loginRomaneio, setRomaneioData, isLoading, error } = useAuth()
  const [localError, setLocalError] = useState(null)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const navigate = useNavigate()

  // Carregar dados salvos quando o componente for montado de forma sanitizada
  useEffect(() => {
    const savedData =
      localStorage.getItem('loginRomaneioData_sw') ||
      localStorage.getItem('romaneioData_sw') ||
      localStorage.getItem('loginRomaneioData')
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData)
        const cleanEmpresa = sanitizeEmpresa(parsed?.empresa)
        const cleanPlaca = sanitizePlaca(parsed?.placa)
        if (cleanEmpresa || cleanPlaca || parsed?.dominio) {
          form.setFieldsValue({
            dominio: parsed?.dominio || undefined,
            empresa: cleanEmpresa || undefined,
            placa: cleanPlaca || undefined
          })
        }
      } catch (error) {
        console.error('Erro ao carregar dados salvos:', error)
      }
    }
  }, [form])

  const handleSubmit = async (values) => {
    setLocalError(null)

    try {
      const cleanDominio = (values?.dominio || '').trim().toUpperCase()
      const cleanEmpresaStr = sanitizeEmpresa(values?.empresa)
      const cleanPlaca = sanitizePlaca(values?.placa)
      const cleanRomaneioStr = sanitizeRomaneio(values?.romaneio)

      if (!cleanDominio || !cleanEmpresaStr || !cleanRomaneioStr || !cleanPlaca) {
        throw new Error('Preencha Domínio, Unidade, Placa e Romaneio com dados válidos.')
      }

      if (cleanPlaca.length !== 7) {
        throw new Error('A placa deve conter exatamente 7 caracteres alfanuméricos.')
      }

      if (cleanEmpresaStr.length > 5) {
        throw new Error('A unidade deve conter no máximo 5 caracteres.')
      }

      // Validar e converter romaneio para número
      const romaneioNum = parseInt(cleanRomaneioStr, 10)

      // Verificar se a conversão é válida e positiva
      if (isNaN(romaneioNum) || romaneioNum <= 0) {
        throw new Error('O romaneio deve ser um número válido.')
      }

      const romaneioData = {
        dominio: cleanDominio,
        empresa: cleanEmpresaStr,
        romaneio: romaneioNum,
        placa: cleanPlaca
      }

      const result = await loginRomaneio(romaneioData)
      if (result.success) {
        // Salvar empresa, placa e domínio no localStorage de forma estritamente sanitizada.
        // Ao reabrir a página de login, o campo Domínio volta preenchido.
        try {
          const payload = JSON.stringify({
            dominio: cleanDominio,
            empresa: cleanEmpresaStr,
            placa: cleanPlaca,
            romaneio: cleanRomaneioStr
          })
          localStorage.setItem('loginRomaneioData_sw', payload)
          localStorage.setItem('romaneioData_sw', payload)
        } catch {
          // Ignora falhas de escrita no localStorage (modo privado/armazenamento cheio)
        }

        form.resetFields()

        // Seta os dados no useAuth()
        if (result.romaneioData) {
          setRomaneioData(result.romaneioData)
        }

        console.log(result.romaneioData)

        if (onSuccess) {
          await onSuccess(result)
        }

        if (autoNavigate) {
          navigate('/onboard')
        }
      } else {
        // Login falhou - tratar erro e exibir popup claro com a mensagem.
        const fallback = 'Dados para login não conferem, usuário ou senha inválidos'
        const errorMessage = (typeof result.error === 'string' ? result.error : result.error?.message) || fallback
        setLocalError(errorMessage)
        setShowErrorModal(true)
        message.error({ content: errorMessage, duration: 5 })
      }
    } catch (err) {
      const errMsg = (typeof err === 'string' ? err : err?.message) || 'Dados para login não conferem.'
      setLocalError(errMsg)
      setShowErrorModal(true)
      message.error({ content: errMsg, duration: 5 })
    }
  }

  const handleFieldChange = () => {
    // Não limpar erros automaticamente para permitir que o modal seja exibido
    // O erro será limpo apenas quando o usuário fechar o modal ou tentar fazer login novamente
  }

  const handleCloseErrorModal = () => {
    setShowErrorModal(false)
    setLocalError(null)
  }

  const displayError = localError || error

  return (
    <Card
      className="login-romaneio-card"
      title={
        <div style={{ textAlign: 'center', width: '100%' }}>
          <Space>
            <TruckOutlined />
            <span>impressão On-Board</span>
          </Space>
        </div>
      }
      styles={{
        header: {
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          borderRadius: '8px 8px 0 0',
          textAlign: 'center',
          padding: '2px 6px',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
      size="small"
      style={{
        width: '100%',
        maxWidth: '400px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >

      {/* Error Display */}
      {displayError && (
        <Alert
          message="Erro de Login"
          description={displayError}
          type="error"
          showIcon
          style={{ marginBottom: '24px', borderRadius: '8px' }}
        />
      )}

      {/* Login Form */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={handleFieldChange}
        autoComplete="off"
        requiredMark={false}
      >
        <Row gutter={[8, 2]}>
          {/* Domínio Field */}
          <Col xs={12} sm={12}>
            <Form.Item
              style={{ marginBottom: 10 }}
              label={
                <Space size={2}>
                  <span style={{ color: '#333', fontWeight: '500', fontSize: 'clamp(10px, 2vw, 14px)' }}>Domínio</span>
                </Space>
              }
              name="dominio"
              normalize={(val) => (val || '').toUpperCase()}
              rules={[{ required: true, message: 'Informe o domínio!' }]}
            >
              <Input
                placeholder=""
                maxLength={10}
                style={{
                  height: 'clamp(35px, 8vw, 45px)',
                  borderRadius: '8px',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  border: '1px solid #434343',
                  textTransform: 'uppercase',
                  backgroundColor: 'white',
                  color: '#000000',
                  boxSizing: 'border-box'
                }}
              />
            </Form.Item>
          </Col>
          {/* Empresa Field */}
          <Col xs={12} sm={12}>
            <Form.Item
              style={{ marginBottom: 10 }}
              label={
                <Space size={2}>
                  <span style={{ color: '#333', fontWeight: '500', fontSize: 'clamp(10px, 2vw, 14px)' }}>Unidade</span>
                </Space>
              }
              name="empresa"
              normalize={(val) => sanitizeEmpresa(val)}
              rules={[
                {
                  required: true,
                  message: 'Informe a unidade!',
                },
                {
                  pattern: /^[A-Z0-9]{1,5}$/,
                  message: 'Até 5 caracteres alfanuméricos!',
                }
              ]}
            >
              <Input
                placeholder=" "
                maxLength={5}
                style={{
                  height: 'clamp(35px, 8vw, 45px)',
                  borderRadius: '8px',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  border: '1px solid #434343',
                  textTransform: 'uppercase',
                  backgroundColor: 'white',
                  color: '#000000',
                  boxSizing: 'border-box'
                }}
                className="no-number-arrows"
              />
            </Form.Item>
          </Col>

          {/* Placa Field */}
          <Col xs={12} sm={12}>
            <Form.Item
              style={{ marginBottom: 10 }}
              label={
                <Space size={2}>
                  <span style={{ color: '#333', fontWeight: '500', fontSize: 'clamp(10px, 2vw, 14px)' }}>Placa</span>
                </Space>
              }
              name="placa"
              normalize={(val) => sanitizePlaca(val)}
              rules={[
                {
                  required: true,
                  message: 'Informe a placa!',
                },
                {
                  pattern: /^[A-Z0-9]{7}$/,
                  message: 'Placa deve conter 7 caracteres alfanuméricos!',
                }
              ]}
            >
              <Input
                placeholder=" "
                type="text"
                maxLength={7}
                style={{
                  height: 'clamp(35px, 8vw, 45px)',
                  borderRadius: '8px',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  border: '1px solid #434343',
                  textTransform: 'uppercase',
                  backgroundColor: 'white',
                  color: '#000000',
                  boxSizing: 'border-box'
                }}
              />
            </Form.Item>
          </Col>

          {/* Romaneio Field */}
          <Col xs={12} sm={12}>
            <Form.Item
              label={
                <Space size={2}>
                  <span style={{ color: '#333', fontWeight: '500', fontSize: 'clamp(10px, 2vw, 14px)' }}>Romaneio</span>
                </Space>
              }
              name="romaneio"
              normalize={(val) => sanitizeRomaneio(val)}
              rules={[
                {
                  required: true,
                  message: 'Informe o romaneio!',
                },
                {
                  pattern: /^\d{1,10}$/,
                  message: 'Apenas números (máx 10 dígitos)!',
                }
              ]}
              style={{ marginBottom: 14 }}
            >
              <Input
                placeholder=" "
                maxLength={10}
                inputMode="numeric"
                style={{
                  height: 'clamp(35px, 8vw, 45px)',
                  borderRadius: '8px',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  border: '1px solid #434343',
                  backgroundColor: 'white',
                  color: '#000000',
                  boxSizing: 'border-box'
                }}
              />
            </Form.Item>
          </Col>
        </Row>


        {/* Action Buttons */}
        <Space style={{ width: '100%' }} direction="vertical">
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isLoading}
            className="login-romaneio-btn"
            style={{
              height: '45px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Entrar com Romaneio
          </Button>

          {onCancel && (
            <Button
              type="default"
              block
              onClick={onCancel}
              style={{
                height: '40px',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'black !important',
              }}
            >
              Cancelar
            </Button>
          )}
        </Space>
      </Form>

      {/* Modal de Erro */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Erro no Login</span>
          </Space>
        }
        open={showErrorModal}
        onOk={handleCloseErrorModal}
        onCancel={handleCloseErrorModal}
        okText="Entendi"
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
        width={400}
      >
        <div style={{ padding: '16px 0' }}>
          <Typography.Text style={{ fontSize: '16px' }}>
            {localError}
          </Typography.Text>
        </div>
      </Modal>
    </Card>
  )
}

export default LoginRomaneio
